import { createContext, useContext, useState, useCallback } from "react";
import { X, Bike, ShoppingBag, ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogPortal } from "@/components/ui/dialog";
import { BUSINESS } from "@/data/rosto";

const OrderModalContext = createContext({ open: () => {} });

export const useOrderModal = () => useContext(OrderModalContext);

const PLATFORMS = [
  {
    id: "glovo",
    name: "Glovo",
    tagline: "Consegna in 30 min",
    href: BUSINESS.links.glovo,
    icon: Bike,
    accent: true,
  },
  {
    id: "justeat",
    name: "Just Eat",
    tagline: "Consegna a domicilio",
    href: BUSINESS.links.justeat,
    icon: ShoppingBag,
  },
  {
    id: "deliveroo",
    name: "Deliveroo",
    tagline: "Rapido e tracciabile",
    href: BUSINESS.links.deliveroo,
    icon: Bike,
  },
];

export const OrderModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);

  return (
    <OrderModalContext.Provider value={{ open }}>
      {children}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogPortal>
          <div
            className="fixed inset-0 z-50 bg-[#0F0F0F]/85 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            data-state={isOpen ? "open" : "closed"}
            aria-hidden="true"
            onClick={() => setIsOpen(false)}
          />
          <DialogContent
            data-testid="order-modal"
            className="!bg-[#1A1311] !border-[#362A26] !p-0 !gap-0 !max-w-md !rounded-2xl overflow-hidden [&>button]:hidden"
          >
            <div className="px-6 pt-7 pb-2 flex items-start justify-between">
              <div>
                <p className="font-label text-[10px] text-[#C86C3F]">
                  Scegli il rider
                </p>
                <h2 className="font-display text-2xl text-[#F5F1E6] mt-1 leading-tight">
                  Ordina a domicilio
                </h2>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                aria-label="Chiudi"
                data-testid="order-modal-close"
                className="w-9 h-9 rounded-full border border-[#362A26] flex items-center justify-center text-[#B2A69E] hover:text-[#F5F1E6] hover:border-[#C86C3F] transition-colors flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 pb-6 pt-4 space-y-2.5">
              {PLATFORMS.map((p) => {
                const Icon = p.icon;
                return (
                  <a
                    key={p.id}
                    href={p.href}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => setIsOpen(false)}
                    data-testid={`order-modal-${p.id}`}
                    className={`group flex items-center gap-4 w-full px-5 py-4 rounded-2xl border transition-all duration-200 ${
                      p.accent
                        ? "bg-[#C86C3F] border-[#C86C3F] hover:bg-[#E68153] text-[#0F0F0F]"
                        : "bg-[#251D1A] border-[#362A26] text-[#F5F1E6] hover:border-[#C86C3F] hover:bg-[#2E2421]"
                    }`}
                  >
                    <span
                      className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        p.accent
                          ? "bg-[#0F0F0F]/15"
                          : "bg-[#1A1311] border border-[#362A26] group-hover:text-[#E68153]"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                    </span>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="font-display text-lg leading-tight">
                        {p.name}
                      </p>
                      <p
                        className={`text-xs mt-0.5 ${
                          p.accent ? "text-[#0F0F0F]/70" : "text-[#B2A69E]"
                        }`}
                      >
                        {p.tagline}
                      </p>
                    </div>
                    <ArrowUpRight className="w-4 h-4 opacity-70 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </a>
                );
              })}
            </div>

            <div className="px-6 pb-6 -mt-1">
              <a
                href={`tel:${BUSINESS.phoneLink}`}
                onClick={() => setIsOpen(false)}
                data-testid="order-modal-phone"
                className="block text-center font-label text-[10px] text-[#B2A69E] hover:text-[#E68153] transition-colors"
              >
                Oppure chiama: {BUSINESS.phoneDisplay}
              </a>
            </div>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </OrderModalContext.Provider>
  );
};

export const OrderButton = ({
  children,
  className = "",
  variant = "primary",
  testid,
  icon: Icon,
}) => {
  const { open } = useOrderModal();
  return (
    <button
      type="button"
      onClick={open}
      data-testid={testid}
      className={className}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {children}
    </button>
  );
};
